import { z } from "zod"

import type { JsonObject } from "../../primitives.js"

import {
  domainEventTypes,
  integrationConnectionTypes,
  integrationProviders,
} from "../../taxonomy.js"
import { ApiJsonObjectSchema } from "./common.js"

export const ApiIntegrationConnectionBodySchema = z.object({
  provider: z.enum(integrationProviders),
  connectionType: z.enum(integrationConnectionTypes),
  status: z.enum(["active", "disabled", "error", "revoked"]).optional(),
  name: z.string().min(1).max(200),
  externalAccountId: z.string().max(200).nullable().optional(),
  secretReference: z.string().max(500).nullable().optional(),
  config: ApiJsonObjectSchema.default({}),
})

export type ApiIntegrationConnectionBody = z.infer<
  typeof ApiIntegrationConnectionBodySchema
>

export const ApiUpdateIntegrationConnectionBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  externalAccountId: z.string().max(200).nullable().optional(),
  secretReference: z.string().max(500).nullable().optional(),
  config: ApiJsonObjectSchema.optional(),
  status: z.enum(["active", "disabled", "error", "revoked"]).optional(),
})

export type ApiUpdateIntegrationConnectionBody = z.infer<
  typeof ApiUpdateIntegrationConnectionBodySchema
>

export const ApiIntegrationConnectionResponseSchema = z.object({
  integrationConnectionId: z.string().uuid(),
  provider: z.string(),
  connectionType: z.string(),
  status: z.string(),
  name: z.string(),
  externalAccountId: z.string().nullable(),
  hasSecretReference: z.boolean(),
  createdByUserId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  disabledAt: z.string().datetime().nullable(),
})

export type ApiIntegrationConnectionResponse = z.infer<
  typeof ApiIntegrationConnectionResponseSchema
>

export const ApiIntegrationConnectionListResponseSchema = z.object({
  integrations: z.array(ApiIntegrationConnectionResponseSchema),
})

export type ApiIntegrationConnectionListResponse = z.infer<
  typeof ApiIntegrationConnectionListResponseSchema
>

// --- webhook endpoints (integration connections with a first-class API) ----

export const ApiWebhookEventTypeSchema = z.enum(domainEventTypes)

export type ApiWebhookEventType = z.infer<typeof ApiWebhookEventTypeSchema>

// --- the event catalog ------------------------------------------------------

/* One source for the event picker in the product and the published reference.
   Every samplePayload is transcribed from the emission site, not invented, so
   what a customer reads here is what arrives. The emission sites are named
   above each entry; change one and change the other. */

export const webhookEventGroups = ["lifecycle", "housekeeping"] as const

export type WebhookEventGroup = (typeof webhookEventGroups)[number]

export const webhookEventGroupLabels = {
  lifecycle: "Document lifecycle",
  housekeeping: "Housekeeping",
} satisfies Record<WebhookEventGroup, string>

export type WebhookEventCatalogEntry = {
  value: ApiWebhookEventType
  label: string
  description: string
  group: WebhookEventGroup
  /** Verbatim the outbox payload, which arrives as the envelope's `data`. */
  samplePayload: JsonObject
}

const sampleEventId = "9c1f4a2e-6b70-4c5a-9f83-1d0e2b7a45c1"
const sampleDocumentId = "3f6b21d8-9a54-4e07-b1c2-8d5f7e04a9b3"
const sampleProcessingRunId = "c4d8e5f1-2a37-4b96-8c0d-5e1f3a7b2d64"

export const webhookEventCatalog: WebhookEventCatalogEntry[] = [
  {
    // packages/db/src/repositories/processing-callbacks.ts, document_processed
    value: "document_processed",
    label: "Document extracted",
    description: "Extraction finished and the results are ready to review.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "document_processed",
      documentId: sampleDocumentId,
      processingRunId: sampleProcessingRunId,
      state: "succeeded",
      derivedState: "needs_review",
    },
  },
  {
    // packages/db/src/repositories/processing-callbacks.ts, flags_created
    value: "flags_created",
    label: "Flags created",
    description: "Extraction raised review flags on a document.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "flags_created",
      documentId: sampleDocumentId,
      processingRunId: sampleProcessingRunId,
      flagCount: 3,
    },
  },
  {
    // packages/db/src/repositories/review.ts, document_approved. The domain
    // event also carries projectionHash; the webhook payload does not.
    value: "document_approved",
    label: "Document approved",
    description: "A reviewer approved the extracted data.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "document_approved",
      documentId: sampleDocumentId,
      approvalId: "7a2c9e14-5d38-4f60-9b21-0c8e6f3a1d57",
    },
  },
  {
    // packages/db/src/repositories/review.ts, export_generated
    value: "export_generated",
    label: "Export generated",
    description: "An export file was created for a document.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "export_generated",
      documentId: sampleDocumentId,
      exportId: "b83f0c67-4e19-42a5-8d7c-1f6a9b2e5304",
      artifactId: "e1a7d340-8c25-4b6f-9013-72d4c8f5b6ea",
      format: "csv",
      isApproved: true,
    },
  },
  {
    // packages/db/src/repositories/review.ts, posting_completed
    value: "posting_completed",
    label: "Send completed",
    description: "A document was sent to an accounting system.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "posting_completed",
      documentId: sampleDocumentId,
      postingId: "5c9e2b78-1a46-4d03-b8f5-6e207c4a9138",
      postingAttemptId: "d20a6f41-9b83-4c17-a5e6-38f0b7d1c294",
    },
  },
  {
    // packages/db/src/repositories/processing-callbacks.ts, batch_completed.
    // Fires once, when the last upload in a batch reaches a terminal state.
    value: "batch_completed",
    label: "Batch completed",
    description: "Every document in one upload finished extracting.",
    group: "lifecycle",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "batch_completed",
      batchId: "a4f1c806-7d29-4e53-91b0-2c6a8f3d5b47",
      documentIds: [sampleDocumentId, "8b5d0e29-3c74-4a18-9f26-71e0d4c8a5b3"],
      documentCount: 2,
      succeededCount: 2,
      failedCount: 0,
    },
  },
  {
    // packages/db/src/repositories/operations.ts, artifact_expired
    value: "artifact_expired",
    label: "File expired",
    description: "A stored file passed its retention window.",
    group: "housekeeping",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "artifact_expired",
      artifactId: "e1a7d340-8c25-4b6f-9013-72d4c8f5b6ea",
      documentId: sampleDocumentId,
      status: "expired",
    },
  },
  {
    // packages/db/src/repositories/operations.ts, purge_completed
    value: "purge_completed",
    label: "Deletion completed",
    description: "A requested deletion finished purging its files.",
    group: "housekeeping",
    samplePayload: {
      eventId: sampleEventId,
      eventType: "purge_completed",
      deletionRequestId: "6d3b8f52-0a17-4c94-8e26-5b1f7a0d3c98",
      documentId: sampleDocumentId,
      artifactId: "e1a7d340-8c25-4b6f-9013-72d4c8f5b6ea",
      purgedArtifactIds: ["e1a7d340-8c25-4b6f-9013-72d4c8f5b6ea"],
      status: "purged",
    },
  },
]

export type WebhookEnvelope = {
  id: string
  deliveryId: string
  type: string
  createdAt: string
  data: JsonObject
}

/**
 * The delivered wire format, in one place. The dispatcher, the test send, the
 * delivery log, and the samples all build envelopes through here, so the
 * shape cannot drift between what we send, what we show, and what we
 * document.
 *
 * `eventId` and `eventType` are repeated inside `data`. That is redundant and
 * deliberate: cleaning it up would break every existing receiver, so the
 * samples show the duplication honestly.
 */
export const buildWebhookEnvelope = (input: {
  /** The domain event id, which is stable across retries and replays. */
  eventId: string
  deliveryId: string
  type: string
  createdAt: string
  data: JsonObject
}): WebhookEnvelope => ({
  id: input.eventId,
  deliveryId: input.deliveryId,
  type: input.type,
  createdAt: input.createdAt,
  data: input.data,
})

/**
 * The envelope `id` a receiver sees. A delivery with no domain event behind it
 * falls back to its own id. One definition, because a receiver deduplicates on
 * this and a replay depends on it being unchanged.
 */
export const webhookEnvelopeId = (input: {
  domainEventId: string | null
  deliveryId: string
}): string => input.domainEventId ?? input.deliveryId

export const webhookEnvelopeSample = (
  entry: WebhookEventCatalogEntry
): string =>
  JSON.stringify(
    buildWebhookEnvelope({
      eventId: sampleEventId,
      deliveryId: "f0c2a739-8e14-4b5d-9036-2a7c8e1f4b60",
      type: entry.value,
      createdAt: "2026-07-24T09:14:03.271Z",
      data: entry.samplePayload,
    }),
    null,
    2
  )

export const ApiCreateWebhookEndpointBodySchema = z.object({
  name: z.string().min(1).max(200),
  url: z.string().url().max(2000),
  /** Empty = subscribe to every event. */
  eventTypes: z.array(ApiWebhookEventTypeSchema).max(20).default([]),
})

export type ApiCreateWebhookEndpointBody = z.infer<
  typeof ApiCreateWebhookEndpointBodySchema
>

export const ApiUpdateWebhookEndpointBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  url: z.string().url().max(2000).optional(),
  eventTypes: z.array(ApiWebhookEventTypeSchema).max(20).optional(),
  status: z.enum(["active", "disabled"]).optional(),
})

export type ApiUpdateWebhookEndpointBody = z.infer<
  typeof ApiUpdateWebhookEndpointBodySchema
>

export const ApiWebhookEndpointResponseSchema = z.object({
  webhookEndpointId: z.string().uuid(),
  name: z.string(),
  url: z.string(),
  eventTypes: z.array(z.string()),
  status: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  disabledAt: z.string().datetime().nullable(),
})

export type ApiWebhookEndpointResponse = z.infer<
  typeof ApiWebhookEndpointResponseSchema
>

export const ApiWebhookEndpointListResponseSchema = z.object({
  webhooks: z.array(ApiWebhookEndpointResponseSchema),
})

export type ApiWebhookEndpointListResponse = z.infer<
  typeof ApiWebhookEndpointListResponseSchema
>

export const ApiCreateWebhookEndpointResponseSchema = z.object({
  webhook: ApiWebhookEndpointResponseSchema,
  /** Shown exactly once; only a reference is stored server-side. */
  secret: z.string(),
})

export type ApiCreateWebhookEndpointResponse = z.infer<
  typeof ApiCreateWebhookEndpointResponseSchema
>

export const ApiRotateWebhookSecretResponseSchema = z.object({
  webhookEndpointId: z.string().uuid(),
  secret: z.string(),
})

export type ApiRotateWebhookSecretResponse = z.infer<
  typeof ApiRotateWebhookSecretResponseSchema
>

export const ApiWebhookTestResponseSchema = z.object({
  delivered: z.boolean(),
  httpStatus: z.number().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
})

export type ApiWebhookTestResponse = z.infer<
  typeof ApiWebhookTestResponseSchema
>

/**
 * How long a claimed delivery stays invisible to other senders. The delivery
 * log reads it to tell "sending now" apart from "waiting for a retry", and
 * packages/db claimDueWebhookDeliveries uses it as its lease default, so it
 * lives here rather than being written down twice.
 */
export const webhookClaimLeaseMs = 2 * 60_000

export const ApiWebhookDeliveryStatusSchema = z.enum([
  "delivered",
  "pending",
  "failed",
  "dead_lettered",
])

export type ApiWebhookDeliveryStatus = z.infer<
  typeof ApiWebhookDeliveryStatusSchema
>

// Mirrors WebhookDeliveryResponseSchema in apps/api operation.schemas.ts;
// change both together or the api-client silently drops the new fields.
export const ApiWebhookDeliveryResponseSchema = z.object({
  webhookDeliveryId: z.string().uuid(),
  /** The envelope id the receiver saw. Stable across retries and replays. */
  eventId: z.string(),
  eventType: z.string(),
  status: z.string(),
  attemptNumber: z.number(),
  nextAttemptAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  /** The request body as sent, so a customer can diff it against their logs. */
  payload: ApiJsonObjectSchema,
  responseStatus: z.number().nullable(),
  /** At most 2 KiB, exactly as the endpoint sent it. Never parsed. */
  responseBody: z.string().nullable(),
  responseDurationMs: z.number().nullable(),
  replayedFromDeliveryId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
})

export type ApiWebhookDeliveryResponse = z.infer<
  typeof ApiWebhookDeliveryResponseSchema
>

export const ApiWebhookDeliveryListResponseSchema = z.object({
  deliveries: z.array(ApiWebhookDeliveryResponseSchema),
})

export type ApiWebhookDeliveryListResponse = z.infer<
  typeof ApiWebhookDeliveryListResponseSchema
>

export const ApiReplayWebhookDeliveryResponseSchema = z.object({
  webhookDeliveryId: z.string().uuid(),
  replayedFromDeliveryId: z.string().uuid(),
})

export type ApiReplayWebhookDeliveryResponse = z.infer<
  typeof ApiReplayWebhookDeliveryResponseSchema
>

// --- QuickBooks connection ---------------------------------------------------

export const ApiQuickBooksStatusResponseSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  /** Intuit stopped accepting the stored tokens; connecting again is the fix.
   * Defaulted so a web build ahead of the API still parses the response. */
  reconnectRequired: z.boolean().default(false),
  /** ISO time the refresh token dies without a reconnect, null when unknown. */
  refreshTokenExpiresAt: z.string().nullable().default(null),
  realmId: z.string().nullable(),
  /** The connected company legal name, null when we could not fetch it. */
  companyName: z.string().nullable(),
  connectionStatus: z.string().nullable(),
  destination: z
    .object({
      postingMappingId: z.string().uuid(),
      name: z.string(),
      bankAccountName: z.string().nullable(),
      offsetAccountName: z.string().nullable(),
      /** Per-category account legs, read back for the mapping editor. */
      categoryAccounts: z.record(
        z.string(),
        z.object({ accountId: z.string(), accountName: z.string() })
      ),
      /**
       * LedgerBox account id to QuickBooks payment account, read back for
       * the mapping editor (PRD: quickbooks-multi-account-posting).
       * Defaulted so a web build ahead of the API still parses.
       */
      paymentAccounts: z
        .record(
          z.string(),
          z.object({ accountId: z.string(), accountName: z.string() })
        )
        .default({}),
    })
    .nullable(),
})

export type ApiQuickBooksStatusResponse = z.infer<
  typeof ApiQuickBooksStatusResponseSchema
>

export const ApiQuickBooksConnectResponseSchema = z.object({
  authorizeUrl: z.string().url(),
})

export type ApiQuickBooksConnectResponse = z.infer<
  typeof ApiQuickBooksConnectResponseSchema
>

export const ApiQuickBooksAccountsResponseSchema = z.object({
  accounts: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      accountType: z.string(),
      accountSubType: z.string().nullable(),
    })
  ),
})

export type ApiQuickBooksAccountsResponse = z.infer<
  typeof ApiQuickBooksAccountsResponseSchema
>

export const ApiQuickBooksDestinationBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  bankAccountId: z.string().min(1).max(50),
  bankAccountName: z.string().min(1).max(200),
  offsetAccountId: z.string().min(1).max(50),
  offsetAccountName: z.string().min(1).max(200),
  /**
   * Category id to QuickBooks account, for per-category posting (PRD:
   * engagement-analysis-layer, phase 4 downstream). Optional and partial:
   * unmapped categories and uncategorised rows post against the offset
   * account above. Names are stored so the editor reads the mapping back
   * without a QuickBooks round trip.
   */
  categoryAccounts: z
    .record(
      z.string().uuid(),
      z.object({
        accountId: z.string().min(1).max(50),
        accountName: z.string().min(1).max(200),
      })
    )
    .optional(),
  /**
   * LedgerBox account id to QuickBooks payment account (PRD:
   * quickbooks-multi-account-posting). Optional; when present and
   * non-empty, statements post against the mapped account and a statement
   * for an unmapped account fails instead of landing in the default
   * register. Absent or empty keeps the single-account behavior.
   */
  paymentAccounts: z
    .record(
      z.string().uuid(),
      z.object({
        accountId: z.string().min(1).max(50),
        accountName: z.string().min(1).max(200),
      })
    )
    .optional(),
})

export type ApiQuickBooksDestinationBody = z.infer<
  typeof ApiQuickBooksDestinationBodySchema
>

export const ApiQuickBooksDestinationResponseSchema = z.object({
  postingMappingId: z.string().uuid(),
  destination: z.string(),
  name: z.string(),
})

export type ApiQuickBooksDestinationResponse = z.infer<
  typeof ApiQuickBooksDestinationResponseSchema
>

export const ApiQuickBooksDisconnectResponseSchema = z.object({
  disconnected: z.boolean(),
})

export type ApiQuickBooksDisconnectResponse = z.infer<
  typeof ApiQuickBooksDisconnectResponseSchema
>

// --- Xero connection ---------------------------------------------------------

/** A stored account choice, read back for the mapping editor. */
const ApiMappedAccountsSchema = z.record(
  z.string(),
  z.object({ accountId: z.string(), accountName: z.string() })
)

export const ApiXeroStatusResponseSchema = z.object({
  /** Whether the Xero OAuth app is configured server-side. */
  configured: z.boolean(),
  connected: z.boolean(),
  /** Xero stopped accepting the stored tokens; connecting again is the fix. */
  reconnectRequired: z.boolean(),
  /** ISO time the refresh token dies without a reconnect, null when unknown. */
  refreshTokenExpiresAt: z.string().nullable(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullable(),
  /** The organisations a sign-in granted, while one waits to be chosen. */
  tenantChoices: z.array(
    z.object({ tenantId: z.string(), tenantName: z.string() })
  ),
  destination: z
    .object({
      postingMappingId: z.string().uuid(),
      name: z.string(),
      bankAccountName: z.string().nullable(),
      offsetAccountName: z.string().nullable(),
      categoryAccounts: ApiMappedAccountsSchema,
      paymentAccounts: ApiMappedAccountsSchema,
    })
    .nullable(),
})

export type ApiXeroStatusResponse = z.infer<typeof ApiXeroStatusResponseSchema>

export const ApiXeroConnectResponseSchema = ApiQuickBooksConnectResponseSchema

export const ApiXeroAccountsResponseSchema = z.object({
  accounts: z.array(
    z.object({
      id: z.string(),
      /** Line items post by code; a bank account may have none. */
      code: z.string().nullable(),
      name: z.string(),
      /** BANK, EXPENSE, CURRLIAB, ... */
      type: z.string(),
      bankAccountType: z.string().nullable(),
      currency: z.string().nullable(),
    })
  ),
})

export type ApiXeroAccountsResponse = z.infer<typeof ApiXeroAccountsResponseSchema>

export const ApiXeroTenantBodySchema = z.object({
  tenantId: z.string().min(1).max(100),
})

export type ApiXeroTenantBody = z.infer<typeof ApiXeroTenantBodySchema>

export const ApiXeroTenantResponseSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
})

export type ApiXeroTenantResponse = z.infer<typeof ApiXeroTenantResponseSchema>

/**
 * The same choices as QuickBooks: a default bank account, the other-side
 * account, and optional per-category and per-statement-account maps. Xero
 * account ids are GUIDs; the handler reads codes from the live chart.
 */
export const ApiXeroDestinationBodySchema = ApiQuickBooksDestinationBodySchema

export type ApiXeroDestinationBody = ApiQuickBooksDestinationBody

export const ApiXeroDestinationResponseSchema = ApiQuickBooksDestinationResponseSchema

export const ApiXeroDisconnectResponseSchema = ApiQuickBooksDisconnectResponseSchema
